import * as React from "react";
import { ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import PrimaryButton from "../components/PrimaryButton";
import { ReducerState } from "AppTypes";
import { IEntry, IEntryDocuments, IDocumentIds } from "../types/appDataTypes";
import TextInput, { ITextInputProps } from "../components/TextInput";
import TextAreaInput from "../components/TextInput/TextAreaInput";
import { userInfoActions, appActions } from "../actions";
import ArtworkPictures from "../components/ArtworkPictures";
import { IEntryViewState } from "../reducers/appReducers";
import TextDialog from "../components/TextDialog";
import TextAreaBlock from "../components/TextAreaBlock";
import api from "../utils/api";
import { isDesktopWidth, digitsOnlyRestrict } from "../utils";
import * as AppTypes from "AppTypes";

const EntryEditor: React.FC = () => {
  const dispatch = useDispatch();
  const {
    activeCountry,
    appId,
    artEntry,
    pictureStore,
    viewState,
    artworkDescriptionLimit,
    artistInfo,
    isCallingApi,
  } = useSelector((store: ReducerState) => ({
    activeCountry: store.userInfo.activeCountry,
    appId: store.userInfo.appId || "",
    artEntry: store.userInfo.artEntry || ([] as IEntry[]),
    isCallingApi: store.app.isCallingApi,
    pictureStore: store.app.pictureStore,
    viewState: (store.app.viewState || {}) as IEntryViewState,
    artworkDescriptionLimit: store.config.appConfig.artworkDescriptionLimit,
    artistInfo: store.userInfo.artistInfo || {},
  }));

  const { entryIndex = 0 } = viewState;
  const [entry, setEntry] = React.useState<IEntry>(artEntry[entryIndex] || {});
  const [isDialogOpen, setDialogOpen] = React.useState(false);
  const [desktopView, setDesktopView] = React.useState(false);
  const originalEntry = artEntry[entryIndex] || {};
  const [isImageUpdated, setIsImageUpdated] = React.useState(false);
  const { langPack, artworkSizeLimit } = useSelector(
    (store: AppTypes.ReducerState) => ({
      langPack: store.config.langPack,
      artworkSizeLimit: store.config.appConfig.artworkSizeLimit,
    })
  );

  const artworkTitleWarning = langPack["artworkTitleWarning"];
  const mediumWarning = langPack["mediumWarning"];

  React.useEffect(() => {
    document.querySelector("html").scrollTop = 0;
    document.querySelector("body").scrollTop = 0;
    const detect = () => setDesktopView(isDesktopWidth());
    detect();
    window.addEventListener("resize", detect);
    return () => window.removeEventListener("resize", detect);
  }, []);

  const cluster: ITextInputProps["cluster"] = {};

  const handleUpdateUserInfo: ITextInputProps["onValidation"] = async (
    validation
  ) => {
    const { isValid, inputId, value } = validation;
    if (isValid) {
      if (value === "" && !(inputId in originalEntry)) {
        const { [inputId]: removedProperty, ...newEntry } = entry;
        setEntry({ ...newEntry });
      } else {
        setEntry({ ...entry, [inputId]: value });
      }
    } else {
      setEntry({ ...entry, [inputId]: "" });
    }
  };

  const setEntryID = (entryId: string) => {
    setEntry({ ...entry, entryId });
  };

  const pictures = pictureStore[entry.entryId] || {
    p1: null,
    s1: null,
    s2: null,
  };

  const base64ImageToBlob = (str: string) => {
    const TYPE_IMAGE = "image/jpeg";

    const pos = str.indexOf(";base64,");
    const type = str.substring(5, pos);
    const b64 = str.substr(pos + 8);
    const imageContent = atob(b64);
    const buffer = new ArrayBuffer(imageContent.length);
    const view = new Uint8Array(buffer);

    for (let n = 0; n < imageContent.length; n++) {
      view[n] = imageContent.charCodeAt(n);
    }

    const blob = new Blob([buffer], { type: type });
    return new File([blob], "image.jpg", { type: TYPE_IMAGE });
  };

  const uploadDocument = async () => {
    let pictureUploaded = false;
    Object.keys(pictures).forEach(async (docId) => {
      const picture = pictures[docId as keyof IEntryDocuments];
      if (picture) {
        pictureUploaded = true;
        const file = base64ImageToBlob(picture);
        const response = await api.uploadDocument({
          file,
          appId,
          entryId: entry.entryId,
          documentId: docId as IDocumentIds,
          entry,
          artistStatus: artistInfo.artistStatus,
          artworkSizeLimit,
        });

        if (response?.status === 200) {
          dispatch(appActions.updateErrorRetryCount(0));
          dispatch(userInfoActions.updateEntry(entry, entryIndex));
          dispatch(appActions.gotoStep("Dashboard"));
        }
      }
    });
    if (!pictureUploaded) {
      dispatch(appActions.updateErrorRetryCount(0));
      dispatch(userInfoActions.updateEntry(entry, entryIndex));
      dispatch(appActions.gotoStep("Dashboard"));
    }
  };

  const saveActionHandler = async () => {
    let isAllValid = true;
    for (let key in cluster) {
      if (!cluster[key]()) isAllValid = false;
    }

    if (entry?.description?.length > artworkDescriptionLimit) {
      isAllValid = false;
    }

    if (isAllValid) {
      if (!entry.entryId) {
        /* Create entry ID only */
        const response = await api.createEntry(appId, entry);
        if (response && response.status === 200 && response.data) {
          entry.entryId = response.data.entryId;
        }
      } 
      /* Update entry values */
      const response = await api.updateEntry(appId, entry);
      if (response && response.status === 200 && response.data) {
        await uploadDocument();
      }
    }
  };

  const handleMaxLength = (e: ChangeEvent<HTMLInputElement>) => {
    let maxNum = 6;
    if (e.target.value.length > maxNum) {
      e.target.value = e.target.value.slice(0, maxNum);
    }
  };

  return (
    <React.Fragment>
      <div className="content content--form wide">
        <p className="app-sub-title">{langPack["artworkEntry"]}</p>

        <ArtworkPictures
          onPhotoUpload={(images) => {
            if (images) {
              setIsImageUpdated(true);
            } else {
              setIsImageUpdated(false);
            }
          }}
          entryIndex={entryIndex}
          onIdRetrieved={setEntryID}
        />

        <TextAreaInput
          inputLabel={langPack["artworkTitle"]}
          initValue={entry.title}
          inputId={"title"}
          validators={[]}
          cluster={cluster}
          onValidation={handleUpdateUserInfo}
          maxLength={40}
          warningText={artworkTitleWarning}
          displayWarning={true}
        />
        {activeCountry === "TH" && (
          <TextAreaInput
            inputLabel={langPack["artworkTitleEn"]}
            initValue={entry.titleEn}
            inputId={"titleEn"}
            validators={[]}
            cluster={cluster}
            onValidation={handleUpdateUserInfo}
            maxLength={40}
            warningText={artworkTitleWarning}
            displayWarning={true}
          />
        )}
        <TextAreaInput
          inputLabel={langPack["labelMedium"]}
          initValue={entry.medium}
          inputId={"medium"}
          validators={[]}
          cluster={cluster}
          onValidation={handleUpdateUserInfo}
          maxLength={40}
          warningText={mediumWarning}
          displayWarning={true}
        />
        <div>
          <label
            data-testid="label"
            className="inputField__label inputField__label--onTop"
          >
            {langPack["labelDimensionDisclaimer"]}
          </label>
          <div className="display-inline">
            <TextInput
              inputLabel={langPack["labelHeight"]}
              type={"number"}
              initValue={entry.height}
              inputId={"height"}
              validators={[]}
              cluster={cluster}
              min={0}
              onInput={handleMaxLength}
              onValidation={handleUpdateUserInfo}
              typingHandler={digitsOnlyRestrict}
            />
            <TextInput
              inputLabel={langPack["labelWidth"]}
              type={"number"}
              initValue={entry.width}
              inputId={"width"}
              validators={[]}
              cluster={cluster}
              min={0}
              onInput={handleMaxLength}
              onValidation={handleUpdateUserInfo}
              typingHandler={digitsOnlyRestrict}
            />
          </div>
        </div>
        {!desktopView && (
          <div onClick={() => setDialogOpen(true)}>
            <TextAreaInput
              inputLabel={langPack["artworkDescription"]}
              initValue={entry.description}
              forceUpdate={{ value: entry.description }}
              inputId={"description"}
              validators={[]}
              maxLength={1500}
              cluster={cluster}
              onValidation={handleUpdateUserInfo}
              editable={false}
            />
          </div>
        )}
      </div>
      {desktopView && (
        <TextAreaBlock
          inputId={"artworkDescription" + entry.entryId}
          title={langPack["artworkDescription"]}
          description={langPack["artworkDescribe"]}
          value={entry.description}
          onSave={(value) => {
            //Write session storage
            const persistStore: AppTypes.ReducerState = JSON.parse(
              window.sessionStorage.getItem("application-store")
            );
            persistStore.userInfo.artEntry[entryIndex] = Object.assign(
              {},
              entry,
              { description: value }
            );
            window.sessionStorage.setItem(
              "application-store",
              JSON.stringify(persistStore)
            );
          }}
          onChange={(value) => {
            if (value === "" && "description" in entry) {
              delete entry.description;
              setEntry(Object.assign({}, entry, { description: value }));
            } else {
              setEntry(Object.assign({}, entry, { description: value }));
            }
          }}
          maxLength={artworkDescriptionLimit}
          style={{ marginTop: 0, paddingTop: 0 }}
          placeholderText={langPack["artworkPlaceholder"]}
        />
      )}
      <div className="action">
        {langPack["competitionDisclaimer"] && (
          <>
            <p className="app-disclaimer">
              {langPack["competitionDisclaimerText"]}
            </p>
            <br />
          </>
        )}
        <PrimaryButton
          text={langPack["buttonSave"]}
          disabled={
            (JSON.stringify(entry) === JSON.stringify(originalEntry) &&
            !isImageUpdated) || isCallingApi
          }
          onClick={saveActionHandler}
        />
      </div>

      <TextDialog
        title={langPack["artworkDescription"]}
        description={langPack["artworkDescribe"]}
        maxLength={artworkDescriptionLimit}
        contentSource={entry.description}
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={(value) => {
          setEntry({ ...entry, description: value });
          setDialogOpen(false);
        }}
        placeholderText={langPack["artworkPlaceholder"]}
      />
    </React.Fragment>
  );
};

export default EntryEditor;
